// frontend/src/components/AI/AIChatWidget.jsx
import React, { useState, useEffect, useRef } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faRobot,
  faTimes,
  faMinimize,
  faMaximize,
  faUser,
  faPaperPlane,
  faSpinner,
  faStar,
  faStar as faStarRegular,
  faEnvelope,faBox,aMoneyBillWave,faTruck,faCalendarCheck,faMoneyBillWave,faCreditCard,faClipboardList,
  faPhone,
  faComments,
  faMessage,
  faBolt,
} from "@fortawesome/free-solid-svg-icons";
import { faWhatsapp } from "@fortawesome/free-brands-svg-icons";
import { useAuth } from "../../context/AuthContext";
import API from "../../api";

const AIChatWidget = ({ clientId = "kenali-enterprise" }) => {
  const { user } = useAuth();
  const [isOpen, setIsOpen] = useState(false);
  const [isMinimized, setIsMinimized] = useState(false);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [sessionId, setSessionId] = useState(null);
  const [showRating, setShowRating] = useState(false);
  const [rating, setRating] = useState(0);
  const [context, setContext] = useState({});
  const [hasInteracted, setHasInteracted] = useState(false);
  const messagesEndRef = useRef(null);
  const inputRef = useRef(null);

  // Quick reply options
  const QUICK_REPLIES = [
    { label: "Products", icon: faBox },
    { label: "Pricing", icon: faMoneyBillWave },
    { label: "Track Order", icon: faTruck },
    { label: "Pre-Order", icon: faCalendarCheck },
    { label: "Payments", icon: faCreditCard },
  ];

  useEffect(() => {
    const savedSession = localStorage.getItem(`chat_session_${clientId}`);
    if (savedSession) {
      setSessionId(savedSession);
    } else {
      const newSessionId = `chat_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`;
      setSessionId(newSessionId);
      localStorage.setItem(`chat_session_${clientId}`, newSessionId);

      setMessages([
        {
          role: "assistant",
          content:
            "Welcome to Kenali Enterprise! I'm your AI assistant. I can help you with product information, pricing, orders, shipping, and more. How can I assist you today?",
          timestamp: new Date().toISOString(),
        },
      ]);
    }
  }, [clientId]);

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  const sendMessage = async (e) => {
    e.preventDefault();
    if (!input.trim() || isLoading) return;

    const userMessage = input.trim();
    setInput("");
    setHasInteracted(true);
    setMessages((prev) => [
      ...prev,
      {
        role: "user",
        content: userMessage,
        timestamp: new Date().toISOString(),
      },
    ]);
    setIsLoading(true);

    try {
      const response = await API.post("/ai/chat", {
        clientId,
        sessionId,
        message: userMessage,
        conversationHistory: messages.slice(-5).map((m) => ({
          role: m.role,
          content: m.content,
        })),
        userContext: {
          userId: user?._id || null,
          userType: user?.userType || "guest",
          email: user?.email || "",
          page: window.location.pathname,
        },
      });

      const { reply, context: responseContext, fallback } = response.data;

      setMessages((prev) => [
        ...prev,
        {
          role: "assistant",
          content: reply,
          timestamp: new Date().toISOString(),
        },
      ]);

      if (responseContext) {
        setContext(responseContext);
      }

      if (!fallback && hasInteracted) {
        setTimeout(() => setShowRating(true), 1500);
      }
    } catch (error) {
      console.error("Chat error:", error);
      const fallbackMessage =
        "I'm sorry, I'm having trouble connecting. Please try again or contact us directly at kenalienterprisesl@gmail.com or +23231030808.";

      setMessages((prev) => [
        ...prev,
        {
          role: "assistant",
          content: fallbackMessage,
          timestamp: new Date().toISOString(),
        },
      ]);
    } finally {
      setIsLoading(false);
    }
  };

  const handleQuickReply = (text) => {
    setInput(`What ${text.toLowerCase()} do you offer?`);
    setTimeout(() => {
      inputRef.current?.focus();
      if (inputRef.current) {
        // Trigger send after a small delay
        const event = new Event("submit", { bubbles: true, cancelable: true });
        inputRef.current.closest("form")?.dispatchEvent(event);
      }
    }, 100);
  };

  const handleRating = async (stars) => {
    setRating(stars);
    try {
      await API.post(`/ai/rate/${sessionId}`, {
        rating: stars,
        clientId,
      });
      setShowRating(false);
    } catch (error) {
      console.error("Rating error:", error);
      setShowRating(false);
    }
  };

  const toggleChat = () => {
    setIsOpen(!isOpen);
    setIsMinimized(false);
    if (!isOpen) {
      setTimeout(() => inputRef.current?.focus(), 300);
    }
  };

  const toggleMinimize = () => {
    setIsMinimized(!isMinimized);
  };

  const formatTime = (timestamp) => {
    return new Date(timestamp).toLocaleTimeString([], {
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  return (
    <>
      {/* Chat Button */}
      {!isOpen && (
        <motion.button
          className="ai-chat-button"
          onClick={toggleChat}
          whileHover={{ scale: 1.1 }}
          whileTap={{ scale: 0.9 }}
          initial={{ scale: 0 }}
          animate={{ scale: 1 }}
          transition={{ type: "spring", stiffness: 300, damping: 20 }}
          aria-label="Open AI chat assistant"
        >
          <FontAwesomeIcon icon={faRobot} />
          <span className="chat-badge">
            <FontAwesomeIcon icon={faBolt} />
          </span>
        </motion.button>
      )}

      {/* Chat Window */}
      <AnimatePresence>
        {isOpen && (
          <motion.div
            className={`ai-chat-window ${isMinimized ? "minimized" : ""}`}
            initial={{ opacity: 0, y: 50, scale: 0.9 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 50, scale: 0.9 }}
            transition={{ type: "spring", stiffness: 300, damping: 25 }}
            role="dialog"
            aria-label="AI Chat Assistant"
          >
            {/* Header */}
            <div className="chat-header">
              <div className="chat-header-info">
                <div className="chat-avatar">
                  <FontAwesomeIcon icon={faRobot} />
                </div>
                <div>
                  <div className="chat-title">Kenali Assistant</div>
                  <div className="chat-status">
                    <FontAwesomeIcon icon={faBolt} />
                    <span>Online • AI Powered</span>
                  </div>
                </div>
              </div>
              <div className="chat-actions">
                <button
                  onClick={toggleMinimize}
                  className="chat-action-btn"
                  aria-label={isMinimized ? "Expand chat" : "Minimize chat"}
                >
                  <FontAwesomeIcon
                    icon={isMinimized ? faMaximize : faMinimize}
                  />
                </button>
                <button
                  onClick={toggleChat}
                  className="chat-action-btn chat-close-btn"
                  aria-label="Close chat"
                >
                  <FontAwesomeIcon icon={faTimes} />
                </button>
              </div>
            </div>

            {!isMinimized && (
              <>
                {/* Messages */}
                <div className="chat-messages">
                  {messages.map((msg, index) => (
                    <div
                      key={index}
                      className={`message ${msg.role === "user" ? "user" : "assistant"}`}
                    >
                      <div className="message-avatar">
                        {msg.role === "user" ? (
                          <FontAwesomeIcon icon={faUser} />
                        ) : (
                          <FontAwesomeIcon icon={faRobot} />
                        )}
                      </div>
                      <div className="message-content">
                        <div className="message-text">{msg.content}</div>
                        <div className="message-time">
                          {formatTime(msg.timestamp)}
                        </div>
                      </div>
                    </div>
                  ))}
                  {isLoading && (
                    <div className="message assistant">
                      <div className="message-avatar">
                        <FontAwesomeIcon icon={faRobot} />
                      </div>
                      <div className="message-content">
                        <div className="typing-indicator">
                          <span></span>
                          <span></span>
                          <span></span>
                        </div>
                      </div>
                    </div>
                  )}
                  <div ref={messagesEndRef} />
                </div>

                {/* Context Cards - Products */}
                {context.products && context.products.length > 0 && (
                  <div className="context-cards">
                    <div className="context-label">
                      <FontAwesomeIcon icon={faBox} />
                      <span>Related Products</span>
                    </div>
                    <div className="context-list">
                      {context.products.slice(0, 3).map((product) => (
                        <a
                          key={product.slug || product._id}
                          href={`/products/${product.slug}`}
                          className="context-card"
                          target="_blank"
                          rel="noopener noreferrer"
                        >
                          <span className="context-card-name">
                            {product.name}
                          </span>
                          <span className="context-card-price">
                            ${product.basePrice?.usd || 0}/
                            {product.unit || "kg"}
                          </span>
                        </a>
                      ))}
                    </div>
                  </div>
                )}

                {/* Context Cards - Orders */}
                {context.orders && context.orders.length > 0 && (
                  <div className="context-cards">
                    <div className="context-label">
                      <FontAwesomeIcon icon={faClipboardList} />
                      <span>Your Orders</span>
                    </div>
                    <div className="context-list">
                      {context.orders.map((order) => (
                        <a
                          key={order.orderNumber}
                          href={`/orders/${order._id}`}
                          className="context-card"
                          target="_blank"
                          rel="noopener noreferrer"
                        >
                          <span className="context-card-name">
                            {order.orderNumber}
                          </span>
                          <span className="context-card-status">
                            {order.status}
                          </span>
                        </a>
                      ))}
                    </div>
                  </div>
                )}

                {/* Rating Prompt */}
                {showRating && (
                  <div className="rating-prompt">
                    <div className="rating-text">
                      <FontAwesomeIcon icon={faMessage} />
                      <span>How helpful was this response?</span>
                    </div>
                    <div className="rating-stars">
                      {[1, 2, 3, 4, 5].map((star) => (
                        <button
                          key={star}
                          onClick={() => handleRating(star)}
                          className={`rating-star ${rating >= star ? "active" : ""}`}
                          aria-label={`Rate ${star} stars`}
                        >
                          <FontAwesomeIcon
                            icon={star <= rating ? faStar : faStarRegular}
                          />
                        </button>
                      ))}
                    </div>
                  </div>
                )}

                {/* Quick Replies */}
                <div className="quick-replies">
                  {QUICK_REPLIES.map((reply) => (
                    <button
                      key={reply.label}
                      className="quick-reply-btn"
                      onClick={() => handleQuickReply(reply.label)}
                    >
                      <FontAwesomeIcon icon={reply.icon} />
                      <span>{reply.label}</span>
                    </button>
                  ))}
                </div>

                {/* Input Form */}
                <form onSubmit={sendMessage} className="chat-input-form">
                  <input
                    ref={inputRef}
                    type="text"
                    placeholder="Type your message..."
                    value={input}
                    onChange={(e) => setInput(e.target.value)}
                    className="chat-input"
                    disabled={isLoading}
                    aria-label="Chat message input"
                  />
                  <button
                    type="submit"
                    className="chat-send-btn"
                    disabled={!input.trim() || isLoading}
                    aria-label="Send message"
                  >
                    {isLoading ? (
                      <FontAwesomeIcon icon={faSpinner} spin />
                    ) : (
                      <FontAwesomeIcon icon={faPaperPlane} />
                    )}
                  </button>
                </form>

                {/* Footer */}
                <div className="chat-footer">
                  <span>AI Assistant</span>
                  <span className="footer-separator">•</span>
                  <a
                    href="mailto:kenalienterprisesl@gmail.com"
                    aria-label="Send email"
                  >
                    <FontAwesomeIcon icon={faEnvelope} />
                  </a>
                  <a href="tel:+23231030808" aria-label="Call us">
                    <FontAwesomeIcon icon={faPhone} />
                  </a>
                  <a
                    href="https://wa.me/23231030808"
                    target="_blank"
                    rel="noopener noreferrer"
                    aria-label="Chat on WhatsApp"
                  >
                    <FontAwesomeIcon icon={faWhatsapp} />
                  </a>
                </div>
              </>
            )}
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};

export default AIChatWidget;
